import React, { FC } from 'react'
import { Color } from 'three'

// The number of times a pattern will be repeated on a face. NOTE: this must be an integer
const PATTERN_REPEAT_COUNT = 11

export enum MapFeaturePattern {
  Flat = 0,
  Stripes = 1,
  Checkers = 2,
  Metallic = 3,
}

const VertexShader = `
  varying vec2 vUv;
  varying vec3 vPosition;
  
  void main() {
    vUv = uv;
    vPosition = position;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    
  }
`

const FragmentShader = `
  uniform float uStripeWidth;
  uniform int uPattern;
  uniform vec3 uColor;
  uniform float uBaseOpacity;
  varying vec3 vPosition;
  varying vec2 vUv;
  
  void main() {
    if (uPattern == ${MapFeaturePattern.Flat}) {
      gl_FragColor = vec4(uColor, uBaseOpacity);
    } else if (uPattern == ${MapFeaturePattern.Stripes}) {
      bool shouldStripe = mod(vPosition.z, uStripeWidth * 2.0) < uStripeWidth;
      float opacityFactor = shouldStripe ? 0.5 : 1.0;
      gl_FragColor = vec4(uColor, uBaseOpacity * opacityFactor);
    } else if (uPattern == ${MapFeaturePattern.Checkers}) {
      float chCount = ${PATTERN_REPEAT_COUNT}.;
      float checkerUvSize = (1. / chCount);
      float actualCheckers = 1. - checkerUvSize;
      float halfChecker = checkerUvSize * 0.5;
      
      vec2 bUv = (vUv * actualCheckers) - halfChecker;
      vec2 cUv = fract((bUv) * (chCount * 0.5)) - 0.5;
      float opacityFactor = clamp(step(cUv.x * cUv.y, 0.), 0.5, 1.);
      gl_FragColor = vec4(uColor, uBaseOpacity * opacityFactor);
    } else if (uPattern == ${MapFeaturePattern.Metallic}) {
      float metallicFactor = 0.7 + 0.9  * vUv.y; 
      vec3 metallicColor = clamp(uColor * metallicFactor, 0.0, 1.0);
      gl_FragColor = vec4(metallicColor, uBaseOpacity);
    }
  }
  
`

interface TilesetMapFeatureStandardMaterialProps {
  // The base color for the material
  color: string
  // The base opacity for the material
  baseOpacity: number

  // The pattern to apply to the material (which will be portrayed by varying the opacity)
  pattern: MapFeaturePattern

  // The length of the "most important" face of the mesh, in the same units as the mesh -- will be used to determine pattern width
  importantFaceLength: number
}
export const TilesetMapFeatureStandardMaterial: FC<
  TilesetMapFeatureStandardMaterialProps
> = (props) => {
  return (
    <shaderMaterial
      key={`tileset-map-feature-standard-material-${props.pattern}-${props.importantFaceLength}-${props.color}-${props.baseOpacity}`}
      vertexShader={VertexShader}
      fragmentShader={FragmentShader}
      transparent={true}
      polygonOffset={true}
      polygonOffsetFactor={-5}
      polygonOffsetUnits={-5}
      uniforms={{
        uColor: { value: colorAsVec3(props.color) },
        uPattern: { value: props.pattern },
        uBaseOpacity: { value: props.baseOpacity },
        uStripeWidth: {
          value: props.importantFaceLength / PATTERN_REPEAT_COUNT,
        },
      }}
    />
  )
}

const colorAsVec3 = (color: string): [number, number, number] => {
  const colorObj = new Color(color)
  return [colorObj.r, colorObj.g, colorObj.b]
}
